import { useCallback } from 'react';

import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';

import type {
	ActionRendererKeyToPayload,
	ActionRendererPredicateKeyToPayload,
	ActionRendererPredicateRegistry,
	ActionRendererPredicateRegistryEntry,
	ActionRendererRegistry,
	ActionRendererRegistryEntry,
	ActionValidationFailure,
	GetOpaqueActionRenderer,
} from './types';

function isKnownActionKey(
	key: string,
	registry: ActionRendererRegistry<ActionRendererKeyToPayload>,
): key is keyof ActionRendererKeyToPayload {
	return Object.prototype.hasOwnProperty.call(registry, key);
}

const defaultGetActionRenderer = {
	ActionRenderer: undefined,
	ActionRendererFallback: undefined,
	readonlyTitle: undefined,
	ReadOnlyActionRenderer: undefined,
	getAdoptedObjects: undefined,
};

const resolveRegistryEntry = <T,>(
	entry: ActionRendererRegistryEntry<T> | undefined,
	actions: MessageAction[],
) => {
	if (!entry) {
		return undefined;
	}

	const {
		renderer,
		fallback,
		readonlyTitle,
		readonlyRenderer,
		validate,
		condition,
		getAdoptedObjects,
	} = entry;

	if (condition && !condition()) {
		return undefined;
	}

	if (validate) {
		for (const action of actions) {
			const result = validate(action);
			if (!result.isValid) {
				throw new ActionValidationFailureErrorImpl(result, action); // invalid payload shape
			}
		}
	}

	return {
		ActionRenderer: renderer,
		ActionRendererFallback: fallback,
		readonlyTitle,
		ReadOnlyActionRenderer: readonlyRenderer,
		getAdoptedObjects,
	};
};

const resolvePredicateEntry = <T,>(
	entry: ActionRendererPredicateRegistryEntry<T> | undefined,
	actions: MessageAction[],
) => {
	if (!entry || !entry.predicate(actions)) {
		return undefined;
	}

	return resolveRegistryEntry(entry, actions);
};

/**
 * A hook that returns a function to get the action renderer for a given action key.
 * @param registry - The registry of action renderers.
 * @param predicateRegistry - The registry of action predicate renderers. Used when action type can't be determined by explicit key.
 * @returns A function to get the action renderer for a given action key.
 * @throws {ActionValidationFailureError} If the action is invalid.
 */
export const useActionRenderer: (
	registry: ActionRendererRegistry<ActionRendererKeyToPayload>,
	predicateRegistry?: ActionRendererPredicateRegistry<ActionRendererPredicateKeyToPayload>,
) => {
	getActionRenderer: GetOpaqueActionRenderer;
} = (
	registry: ActionRendererRegistry<ActionRendererKeyToPayload>,
	predicateRegistry?: ActionRendererPredicateRegistry<ActionRendererPredicateKeyToPayload>,
) => {
	const getActionRenderer: GetOpaqueActionRenderer = useCallback(
		(actionKey: string, actions: MessageAction[]) => {
			if (!actions?.length) {
				return defaultGetActionRenderer;
			}

			if (isKnownActionKey(actionKey, registry)) {
				const actionsWithKey = actions.filter((action) => action.key === actionKey);
				if (actionsWithKey.length) {
					const entry = registry[actionKey];
					const keyedEntry = resolveRegistryEntry(
						entry as ActionRendererRegistryEntry<ActionRendererKeyToPayload[typeof actionKey]>,
						actionsWithKey,
					);
					if (keyedEntry) {
						return keyedEntry;
					}
				}
			}

			for (const predicate of Object.values(predicateRegistry || {})) {
				const predicateMatch = resolvePredicateEntry(predicate, actions);
				if (predicateMatch) {
					return predicateMatch;
				}
			}

			return defaultGetActionRenderer;
		},
		[registry, predicateRegistry],
	);

	return { getActionRenderer };
};

export interface ActionValidationFailureError extends Error {
	validationFailure: ActionValidationFailure;
	action: MessageAction;
}

class ActionValidationFailureErrorImpl extends Error implements ActionValidationFailureError {
	validationFailure: ActionValidationFailure;
	action: MessageAction;

	constructor(validationFailure: ActionValidationFailure, action: MessageAction) {
		super();
		this.validationFailure = validationFailure;
		this.action = action;
	}
}

export function isActionValidationFailureError(
	error: unknown,
): error is ActionValidationFailureError {
	return (
		error !== null && typeof error === 'object' && 'validationFailure' in error && 'action' in error
	);
}
